import {Http} from '../../../Data/config/Http';
import {BaseResponse} from '../../../Data/types/BaseResponse';
import {MarketerDTO, MarketerInfoDTO} from '../models/marketer.dto';
import {Marketer} from '../models/marketer.model';
import {mapMarketerDTOToModel} from '../mappers/marketer.mapper';
import {CreateMarketerRequest, CreateMarketerRequestDTO, CreateMarketerResponse} from '../models/CreateMarketer';
import {UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest, UpdateMarketerResponse} from '../models/UpdateMarketer';
import {API_ROUTES} from '../../../constants/apiRoutes';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

export interface MarketerQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

// Default query parameters using PAGINATION_CONFIG
export const DEFAULT_MARKETER_QUERY_PARAMS: MarketerQueryParams = {
  page: 1,
  limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  search: '',
  status: ''
};

export interface MarketerBounds {
  sw: { lat: number; lng: number };
  ne: { lat: number; lng: number };
}

export const MarketerAPIDataSource = {
  getAll: async (params?: MarketerQueryParams): Promise<{ items: Marketer[]; pagination?: any }> => {
    try {
      // Use MARKET_DEVELOPERS endpoint for listing (legacy endpoint)
      const response = await Http.get<BaseResponse<MarketerDTO[]>>(API_ROUTES.MARKETERS.MARKET_DEVELOPERS, {params});
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب المسوقين');
      const mappedData = Array.isArray(items) ? items.map(mapMarketerDTOToModel) : [];
      return {
        items: mappedData,
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getAllWithoutPagination: async (): Promise<{ items: Marketer[]; pagination?: any }> => {
    try {
      // limit=all → backend returns the complete set in one response (capped
      // server-side). Replaces the old limit=1000 + multi-page-loop guesswork.
      const response = await Http.get<BaseResponse<MarketerDTO[]>>(API_ROUTES.MARKETERS.MARKET_DEVELOPERS, {
        params: {
          page: 1,
          limit: 'all'
        }
      });
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب المسوقين');
      const mappedData = Array.isArray(items) ? items.map(mapMarketerDTOToModel) : [];

      return {
        items: mappedData,
        pagination: raw.pagination || {
          page: 1,
          limit: mappedData.length,
          total: mappedData.length,
          totalPages: 1
        }
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  create: async (marketerData: CreateMarketerRequest): Promise<Marketer> => {
    try {
      // Convert camelCase to snake_case for API
      const apiData: CreateMarketerRequestDTO = {
        username: marketerData.username,
        full_name: marketerData.fullName,
        email: marketerData.email,
        phone_number: marketerData.phoneNumber,
        password: marketerData.password
      };

      // Add optional location fields only if provided
      if (marketerData.latitude !== undefined) {
        apiData.latitude = marketerData.latitude;
      }
      if (marketerData.longitude !== undefined) {
        apiData.longitude = marketerData.longitude;
      }
      if (marketerData.territory !== undefined) {
        apiData.territory = marketerData.territory;
      }
      if (marketerData.vehicleType !== undefined) {
        apiData.vehicle_type = marketerData.vehicleType;
      }

      // Use new dedicated marketer endpoint
      const response = await Http.post(API_ROUTES.MARKETERS.BASE, apiData);
      const raw = response.data as BaseResponse<CreateMarketerResponse>;
      const responseData = unwrapResponse(raw, 'فشل في إنشاء المسوق', [200, 201]);

      // Convert response to MarketerDTO format for mapping
      const marketerDTO: MarketerDTO = {
        id: responseData.id,
        username: responseData.username,
        full_name: responseData.full_name,
        email: responseData.email,
        phone_number: responseData.phone_number,
        status: responseData.status,
        created_at: responseData.created_at,
        marketer_info: responseData.marketer_info
      };

      const mappedMarketer = mapMarketerDTOToModel(marketerDTO);
      return mappedMarketer;
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getNearbyInBounds: async (
    bounds: MarketerBounds,
    includeDetails: boolean = false,
    role: string = 'market_developer',
    onlyActive: boolean = true,
    limit: number = 1000
  ): Promise<{ items: MarketerDTO[]; pagination?: any }> => {
    try {

      const params = {
        sw_lat: bounds.sw.lat.toString(),
        sw_lng: bounds.sw.lng.toString(),
        ne_lat: bounds.ne.lat.toString(),
        ne_lng: bounds.ne.lng.toString(),
        role,
        includeDetails: includeDetails.toString(),
        onlyActive: onlyActive.toString(),
        limit: Math.min(limit, 1000).toString() // Max 1000 as per API
      };


      const response = await Http.get<BaseResponse<MarketerDTO[]>>(API_ROUTES.MARKETERS.NEARBY, {params});
      const raw = response.data as BaseResponse<MarketerDTO[]> & { meta?: { limit?: number; total?: number } };
      const items = unwrapResponse(raw, 'Failed to fetch marketers');

      return {
        items: Array.isArray(items) ? items : [],
        pagination: {
          page: 1,
          limit: raw.pagination?.limit || raw.meta?.limit || limit,
          total: raw.pagination?.total || raw.meta?.total || items?.length || 0,
          totalPages: Math.ceil(((raw.pagination?.total || raw.meta?.total || items?.length || 0)) / (raw.pagination?.limit || raw.meta?.limit || limit))
        }
      };

    } catch (error: any) {

      // Log the full error for debugging
      if (error.response?.data) {
      }

      // If the main endpoint fails, return empty array for now
      // This ensures the UI doesn't break while the backend is being fixed
      return {
        items: [],
        pagination: {
          page: 1,
          limit,
          total: 0,
          totalPages: 0
        }
      };
    }
  },

  getById: async (id: string): Promise<BaseResponse<MarketerDTO>> => {
    try {
      const response = await Http.get<BaseResponse<MarketerDTO>>(API_ROUTES.USERS.BY_ID(id));
      const data = response.data;

      return {
        stat_code: data.stat_code || 200,
        stat_message: data.stat_message || 'success',
        data: data.data
      };
    } catch (error) {
      throw error;
    }
  },

  // جلب المسوقين في منطقة جغرافية محددة
  getNearbyMarketers: async (bounds: MarketerBounds): Promise<{ items: Marketer[]; pagination?: any }> => {
    try {

      const params = {
        sw_lat: bounds.sw.lat,
        sw_lng: bounds.sw.lng,
        ne_lat: bounds.ne.lat,
        ne_lng: bounds.ne.lng,
        limit: 1000
      };

      const response = await Http.get<BaseResponse<MarketerDTO[]>>(API_ROUTES.MARKETERS.NEARBY, {params});
      const raw = response.data;

      const items = unwrapResponse(raw, 'حدث خطأ في جلب المسوقين القريبين');
      const mappedData = Array.isArray(items)
        ? items.map(mapMarketerDTOToModel)
        : [];


      return {
        items: mappedData,
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // حذف المسوق
  delete: async (id: string | number): Promise<BaseResponse<{ success: boolean }>> => {
    try {
      const response = await Http.delete<BaseResponse<{ success: boolean }>>(API_ROUTES.USERS.BY_ID(id.toString()));
      return unwrapResponse(response.data, 'حدث خطأ في حذف المسوق');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // تحديث بيانات المسوق
  updateProfile: async (id: number, profileData: UpdateMarketerProfileRequest): Promise<UpdateMarketerResponse> => {
    try {
      const response = await Http.put<BaseResponse<UpdateMarketerResponse>>(API_ROUTES.USERS.PROFILE(id.toString()), profileData);
      return unwrapResponse(response.data, 'حدث خطأ في تحديث بيانات المسوق');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // تغيير كلمة مرور المسوق
  updatePassword: async (id: number, passwordData: UpdateMarketerPasswordRequest): Promise<UpdateMarketerResponse> => {
    try {
      const response = await Http.put<BaseResponse<UpdateMarketerResponse>>(API_ROUTES.USERS.PASSWORD(id.toString()), passwordData);
      return unwrapResponse(response.data, 'حدث خطأ في تغيير كلمة المرور');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
